import { useCallback, useEffect, useMemo, useRef, type JSX, type Ref } from "react"
import useMountEffect from "hooks/mount-effect"
import { DockviewReact, themeLightSpaced, type DockviewApi, type DockviewReadyEvent, type IWatermarkPanelProps, type IDockviewPanelProps, type SerializedDockview, IDockviewHeaderActionsProps, type IDockviewPanelHeaderProps } from "dockview"
import { get, set, isEmpty } from "lodash"
import Loading from "components/loading"
import CustomTabComponent from "./custom-tab"
import RightControls from "./right-controls"
import DefaultWatermark from "./watermark"
import { applyPanelDefinitions, applyPanelChanges } from "./utils"
import { getPanelOrderFromLayout } from "connected-components/dashboard/helpers/layout-order"
import { generateStablePanels, limitComponentsByMaxPanels } from "./template-utils"
import { DEFAULT_TEMPLATE } from "./templates"
import type { OpLayoutEngineRendererActions, OpLayoutEngineRendererProps, OPDockviewPersistence, OPDockviewPanelDefinition, OPLayoutPanel, TabIconComponent } from "./types"

import "dockview/dist/styles/dockview.css"
import "./style.scss"

function useLayoutActions(
  persistence: OPDockviewPersistence | undefined,
  panels: readonly OPDockviewPanelDefinition[],
) {
  return useMemo(() => {
    const persistLayout = (api: DockviewApi) => {
      if (!persistence) {
        return
      }
      persistence.save(api.toJSON())
    }

    const applyDefaults = (api: DockviewApi) => {
      applyPanelDefinitions(api, panels)
      persistLayout(api)
    }

    const resetLayout = (api: DockviewApi) => {
      persistence?.clear()
      applyDefaults(api)
    }

    const loadLayout = (api: DockviewApi) => {
      if (!persistence) {
        applyDefaults(api)
        return
      }

      if (persistence.loading) {
        return
      }

      let serialized: SerializedDockview | null | undefined
      try {
        serialized = persistence.load()
      } catch {
        applyDefaults(api)
        return
      }

      if (!serialized || isEmpty(serialized)) {
        applyDefaults(api)
        return
      }

      try {
        api.fromJSON(serialized)
      } catch {
        persistence.clear()
        applyDefaults(api)
      }
    }

    return { resetLayout, loadLayout, persistLayout, loading: persistence?.loading ?? false }
  }, [persistence, panels])
}

// Dockview expects component ids to be unique per renderer; we map each panel id to the renderer it should use.
interface ResolvedComponents {
  definitions: OPDockviewPanelDefinition[]
  registry: Record<string, (props: IDockviewPanelProps) => JSX.Element>
}

/**
 * - Dockview requires that every AddPanelOptions.component key maps to a renderer function in the components registry. This function produces:
 *   1) a registry of component ids -> renderer functions (This is where we need to create unique component ids)
 *   2) a rewritten set of panel definitions whose `component` fields reference those registry keys
 *
 * Why not a single component registry only?
 * - The registry only describes "how to render". We still need the panel definitions to describe
 *   "what and where" (ids, positions, sizes). Both are required to build the layout.
 *
 * How will this work:
 * - We first index panelId -> renderer from the calculated panels (these contain the actual renderers, we pass these renderers to the engine).
 * - For each template, we derive a registry id. If multiple definitions request the same component id, we suffix with `__<n>` to keep each registry entry unique.
 * - We then rewrite the definition.component to the unique registry id, and expose both the rewritten
 *   definitions and the registry for Dockview
 */
function resolveComponentDefinitions(
  panels: readonly OPLayoutPanel[],
  definitions: readonly OPDockviewPanelDefinition[],
): ResolvedComponents {
  const rendererByPanelId: Record<string, (props: IDockviewPanelProps) => JSX.Element> = {}
  for (const panel of panels) {
    rendererByPanelId[panel.panelId] = panel.component.renderer
  }

  const requestedIdUsage: Record<string, number> = {}
  const componentToRendererRegistry: Record<string, (props: IDockviewPanelProps) => JSX.Element> = {}

  const componentDefinitions = definitions.map(def => {
    const requestedComponentId = def.component
    const count = get(requestedIdUsage, [requestedComponentId], 0)
    set(requestedIdUsage, [requestedComponentId], count + 1)

    const registryComponentId = count === 0
      ? requestedComponentId
      : `${requestedComponentId}__${count}`

    componentToRendererRegistry[registryComponentId] = rendererByPanelId[def.id]

    return { ...def, component: registryComponentId }
  })

  return { definitions: componentDefinitions, registry: componentToRendererRegistry }
}

function assignLayoutRef(ref: Ref<OpLayoutEngineRendererActions | null>, value: OpLayoutEngineRendererActions | null): void {
  if (typeof ref === "function") {
    ref(value)
    return
  }

  if (ref && "current" in ref) {
    ref.current = value
  }
}

function getInitiallyMissingPanelIds(
  definitionIds: Set<string>,
  loadedIds: Set<string>,
): Set<string> {
  return new Set(Array.from(definitionIds).filter((definitionId) => !loadedIds.has(definitionId)))
}

export default function OpLayoutEngineRenderer({
  components,
  template,
  persistence,
  className,
  layoutRef,
  onLayoutReady,
  removePanel,
}: OpLayoutEngineRendererProps): JSX.Element {
  const apiRef = useRef<DockviewApi | null>(null)
  const layoutChangeDisposableRef = useRef<{ dispose: () => void } | null>(null)
  const prevPanelIdsRef = useRef<Set<string>>(new Set())
  const initialMissingPanelIdsRef = useRef<Set<string>>(new Set())
  const isInitialLoadRef = useRef(true)
  const hasLoadedLayoutRef = useRef(false)

  const templateFn = template || DEFAULT_TEMPLATE

  const limitedComponents = useMemo(
    () => limitComponentsByMaxPanels(components, templateFn.maxPanels),
    [components, templateFn.maxPanels],
  )

  const panels = useMemo<OPLayoutPanel[]>(
    () => generateStablePanels(limitedComponents),
    [limitedComponents],
  )

  // Create a mapping from panel IDs to their metadata
  const panelMetadata = useMemo(() => {
    const metadata: Record<string, {
      removable: boolean
      removePanel?: (panelId: string) => void
      editable: boolean
      onUpdateTitle?: (panelId: string, newTitle: string) => void
      icon?: TabIconComponent
    }> = {}
    panels.forEach((panel) => {
      metadata[panel.panelId] = {
        removable: panel.component.removable ?? false,
        removePanel: panel.component.removePanel,
        editable: panel.component.editable ?? false,
        onUpdateTitle: panel.component.onUpdateTitle,
        icon: panel.component.icon,
      }
    })
    return metadata
  }, [panels])

  const rawPanelDefinitions = useMemo<OPDockviewPanelDefinition[]>(
    () => templateFn.compile(panels),
    [templateFn, panels],
  )

  const { definitions: panelDefinitions, registry: componentRegistry } = useMemo(
    () => resolveComponentDefinitions(panels, rawPanelDefinitions),
    [panels, rawPanelDefinitions],
  )

  const { resetLayout, loadLayout, persistLayout, loading } = useLayoutActions(persistence, panelDefinitions)

  const handleReady = useCallback((event: DockviewReadyEvent) => {
    if (layoutChangeDisposableRef.current) {
      layoutChangeDisposableRef.current.dispose()
      layoutChangeDisposableRef.current = null
    }

    apiRef.current = event.api
    layoutChangeDisposableRef.current = event.api.onDidLayoutChange(() => {
      persistLayout(event.api)
    })

    if (!persistence || !persistence.loading) {
      loadLayout(event.api)
      hasLoadedLayoutRef.current = true
    } else {
      hasLoadedLayoutRef.current = false
    }

    // Track actual panel IDs after loading to keep incremental updates in sync
    const initialIds = new Set(event.api.panels.map(panel => panel.id))
    const definitionIds = new Set(panelDefinitions.map(panel => panel.id))
    const initiallyMissingIds = getInitiallyMissingPanelIds(definitionIds, initialIds)

    prevPanelIdsRef.current = initialIds
    initialMissingPanelIdsRef.current = initiallyMissingIds
    isInitialLoadRef.current = false
  }, [loadLayout, persistLayout, persistence, panelDefinitions])

  useMountEffect(() => {
    apiRef.current = null
    layoutChangeDisposableRef.current?.dispose()
    layoutChangeDisposableRef.current = null
  })

  // Apply layout again when persistence changes and is not loading
  useEffect(() => {
    if (hasLoadedLayoutRef.current) {
      return
    }

    const api = apiRef.current
    if (!api || !persistence || persistence.loading) {
      return
    }

    loadLayout(api)
    hasLoadedLayoutRef.current = true
    // Sync tracked panel IDs with actual layout after load
    const loadedIds = new Set(api.panels.map(panel => panel.id))
    const definitionIds = new Set(panelDefinitions.map(panel => panel.id))
    const initiallyMissingIds = getInitiallyMissingPanelIds(definitionIds, loadedIds)

    prevPanelIdsRef.current = loadedIds
    initialMissingPanelIdsRef.current = initiallyMissingIds
  }, [persistence, loadLayout, panelDefinitions])

  // Watch for panel changes after initial load and apply incrementally
  useEffect(() => {
    const api = apiRef.current
    if (!api || isInitialLoadRef.current) return

    const currentIds = new Set(panelDefinitions.map(p => p.id))
    const prevIds = prevPanelIdsRef.current

    // Detect changes
    const addedIds = Array.from(currentIds).filter(id => !prevIds.has(id))
    const removedIds = Array.from(prevIds).filter(id => !currentIds.has(id))

    if (addedIds.length === 0 && removedIds.length === 0) {
      return // No changes
    }

    // Find full definitions for added panels
    const missingAtInitialLoad = initialMissingPanelIdsRef.current
    const newAddedIds = addedIds.filter(id => !missingAtInitialLoad.has(id))
    const added = panelDefinitions.filter(p => newAddedIds.includes(p.id))

    // Apply changes incrementally
    applyPanelChanges(api, { added, removed: removedIds })

    // Update tracking
    prevPanelIdsRef.current = currentIds
    persistLayout(api)
  }, [panelDefinitions, persistLayout])

  // Sync panel titles when component definitions change
  useEffect(() => {
    const api = apiRef.current
    if (!api || isInitialLoadRef.current) return

    // Update titles for existing panels
    panels.forEach((panel) => {
      const dockviewPanel = api.getPanel(panel.panelId)
      if (dockviewPanel && dockviewPanel.api.title !== panel.component.title) {
        dockviewPanel.api.setTitle(panel.component.title)
      }
    })
  }, [panels])

  const actions = useMemo<OpLayoutEngineRendererActions>(() => ({
    resetLayout: api => resetLayout(api),
    getApi: () => apiRef.current,
    getPanelOrder: () => getPanelOrderFromLayout(apiRef.current?.toJSON?.() ?? null),
  }), [resetLayout])

  const WatermarkComponent = useMemo(() => {
    const Watermark = (props: IWatermarkPanelProps) => <DefaultWatermark {...props} onReset={resetLayout} />
    Watermark.displayName = "WatermarkComponent"
    return Watermark
  }, [resetLayout])

  const RightControlsComponent = useCallback((props: IDockviewHeaderActionsProps) => {
    return (
      <RightControls
        {...props}
        persistLayout={() => persistLayout(props.containerApi)}
      />
    )
  }, [persistLayout])

  const CustomTabComponentWithMetadata = useCallback((props: IDockviewPanelHeaderProps) => {
    const metadata = panelMetadata[props.api.id]
    return (
      <CustomTabComponent
        {...props}
        removable={metadata?.removable}
        removePanel={removePanel ?? metadata?.removePanel}
        editable={metadata?.editable}
        onUpdateTitle={metadata?.onUpdateTitle}
        icon={metadata?.icon}
      />
    )
  }, [panelMetadata, removePanel])

  // Here we are assigning the actions to the layout ref when it is mounted, and cleaning up when it is unmounted
  useEffect(() => {
    assignLayoutRef(layoutRef, actions)
    onLayoutReady?.(actions)

    return () => {
      assignLayoutRef(layoutRef, null)
      onLayoutReady?.(null)
    }
  }, [layoutRef, onLayoutReady, actions])

  if (loading) {
    return <Loading />
  }

  return (
    <DockviewReact
      onReady={handleReady}
      components={componentRegistry}
      defaultTabComponent={CustomTabComponentWithMetadata}
      rightHeaderActionsComponent={RightControlsComponent}
      watermarkComponent={WatermarkComponent}
      theme={themeLightSpaced}
      className={className}
    />
  )
}
